import {
  NILoyaltyLoginIcon,
  NILoyaltyOfferIcon,
} from '@native-instruments/webex-ni';
import { Link, useLoaderData } from 'react-router';

import {
  getIsShippable,
  getLoyaltyVariant,
} from 'dotcom-core/src/helpers/product';
import { AddToCartButton } from '../AddToCartButton';
import { useIntl } from '~/helpers/intl';
import { isTruthy } from '~/helpers/predicate';

import clsx from 'clsx';

import type { loader } from '../../route';
import { PriceListing } from '../PriceListing';
import { ProductAvailability } from '../ProductAvailability';

export function ProductSummary() {
  const { t, tPrice, tReact } = useIntl();

  const {
    linkedProducts,
    product,
    variant,
    countryRelatedInfo,
    isAuthenticated,
    addToCartSku,
    loginUrl,
  } = useLoaderData<typeof loader>();

  const loyaltyVariant = getLoyaltyVariant(product);
  const isOnSale = variant?.onSale ?? product.onSale;
  const shouldShowLabel = isTruthy(product.label) || isOnSale;
  const isHardware = getIsShippable({ product, variant });

  return (
    <>
      {shouldShowLabel && (
        <div
          className="rounded bg-sale py-0.5 px-1 text-xs font-medium text-grey-10 w-fit mb-1"
          data-testid="product-label"
        >
          {isTruthy(product.label)
            ? product.label
            : t('catalog.filterOnSale', {
                fallback: 'On sale',
              })}
        </div>
      )}

      <h1 className="webex-h2 mb-1" data-testid="product-title">
        {product.name}
      </h1>

      <p
        className="text-grey-70 text-ellipsis mb-3 text-xs"
        data-testid="product-desc"
      >
        {product.description}
      </p>

      {linkedProducts && linkedProducts.length >= 1 && isHardware && (
        <div
          data-testid="linked-products"
          className="flex mb-3 flex-col gap-1.5"
        >
          {linkedProducts.map(
            ({ slug, name, description, currency, value }) => {
              const isCurrentProduct = slug === product.slug;

              return (
                <Link
                  data-testid="linked-product"
                  key={slug}
                  to={`../${slug}`}
                  relative="path"
                  className={clsx(
                    isCurrentProduct &&
                      'outline outline-1 outline-grey-90 border-grey-90',
                    'flex place-content-between rounded border border-solid bg-white p-3 border-grey-40 py-2 px-3 gap-3 hover:outline hover:outline-1 hover:outline-grey-90 hover:border-grey-90',
                  )}
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-base pb-1">{name}</span>
                    <span className="text-2xs">{description}</span>
                  </div>
                  <span className="self-center font-medium text-base">
                    {currency &&
                      tPrice({
                        price: {
                          currency,
                          value,
                        },
                      })}
                  </span>
                </Link>
              );
            },
          )}
        </div>
      )}

      {isAuthenticated && isTruthy(loyaltyVariant?.relatedOwnedProductName) && (
        <div
          data-testid="loyalty-offer-auth"
          className="my-3 p-2 rounded-sm bg-grey-20 w-full text-xs flex justify-content items-center"
        >
          <div className="mr-1">
            <NILoyaltyOfferIcon />
          </div>

          {tReact('pdp.loyaltyBannerSuccess', {
            fallback:
              'Because you own {productName}, you qualify for the {loyaltyOfferType}.',
            values: {
              productName: (
                <span className="font-medium" data-testid="own-product">
                  {loyaltyVariant.relatedOwnedProductName}
                </span>
              ),
              loyaltyOfferType: (
                <span className="font-medium" data-testid="license-label">
                  {loyaltyVariant.licenseLabel}
                </span>
              ),
            },
          })}
        </div>
      )}

      {!isAuthenticated && isTruthy(product.price?.loyalty) && (
        <div
          data-testid="loyalty-offer-unauth"
          className="my-3 p-2 rounded bg-grey-20 w-full text-xs flex justify-content items-center"
        >
          <div className="mr-1">
            <NILoyaltyLoginIcon />
          </div>

          {tReact('pdp.loyaltybanner', {
            fallback:
              'Loyalty pricing from {value}. {loginLink} to see if you qualify.',
            values: {
              value: tPrice({ price: product.price.loyalty }),
              loginLink: (
                <a
                  href={loginUrl}
                  className="font-medium underline"
                  data-testid="loyalty-login-link"
                >
                  {t('common.link.login', {
                    fallback: 'Login',
                  })}
                </a>
              ),
            },
          })}
        </div>
      )}

      {variant?.ownedByUser === true ? (
        <p
          className="flex justify-center text-xs text-success font-medium py-1"
          data-testid="already-own-msg"
        >
          {t('pages.whatsnewforme.alreadyOwn', {
            fallback: 'You already own this product.',
          })}
        </p>
      ) : (
        <ProductAvailability
          variant={variant}
          product={product}
          countryRelatedInfo={countryRelatedInfo}
        />
      )}

      <div className="hidden lg:block">
        <div className="my-3 w-full flex items-center text-sm font-bold justify-center">
          <PriceListing product={product} variant={variant} />
        </div>

        <AddToCartButton
          product={product}
          variant={variant}
          data-testid={`add-to-cart-desktop-${addToCartSku}`}
        />
      </div>
    </>
  );
}
